import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MobileMenuTrigger } from '@/components/Layout';
import { 
  ArrowLeft, Bell, CheckCheck, RefreshCw, MessageCircle, 
  DollarSign, Calendar, Trophy, Flame, AlertCircle, AlertTriangle,
  ChevronRight, User, Heart, Trash2, X, Phone, Users, TrendingUp,
  Clock, Zap, Filter
} from 'lucide-react';
import { toast } from 'sonner';

// Priority config
const PRIORITY_CONFIG = {
  high: { 
    color: 'border-l-red-500 bg-red-50', 
    badge: 'bg-red-100 text-red-700',
    label: 'Urgent'
  },
  medium: { 
    color: 'border-l-amber-500 bg-amber-50/50', 
    badge: 'bg-amber-100 text-amber-700',
    label: 'Important'
  },
  low: { 
    color: 'border-l-slate-300 bg-white', 
    badge: 'bg-slate-100 text-slate-600',
    label: 'Info'
  }
};

// Get icon for notification type
const getNotificationIcon = (type, priority) => {
  const iconMap = {
    // HIGH Priority
    followup_overdue: { icon: AlertTriangle, bg: 'bg-red-100', color: 'text-red-600' },
    booking_rejected: { icon: X, bg: 'bg-red-100', color: 'text-red-600' },
    lead_vdnb: { icon: AlertCircle, bg: 'bg-red-100', color: 'text-red-600' },
    
    // MEDIUM Priority
    followup_due_today: { icon: Phone, bg: 'bg-amber-100', color: 'text-amber-600' },
    booking_approved: { icon: CheckCheck, bg: 'bg-emerald-100', color: 'text-emerald-600' },
    leaderboard_change: { icon: Trophy, bg: 'bg-amber-100', color: 'text-amber-600' },
    warm_lead_inactive: { icon: Clock, bg: 'bg-orange-100', color: 'text-orange-600' },
    morning_digest: { icon: Zap, bg: 'bg-purple-100', color: 'text-purple-600' },
    
    // LOW Priority (Social)
    feed_comment: { icon: MessageCircle, bg: 'bg-blue-100', color: 'text-blue-600' },
    feed_tag: { icon: User, bg: 'bg-indigo-100', color: 'text-indigo-600' },
    feed_reaction: { icon: Flame, bg: 'bg-orange-100', color: 'text-orange-600' },
    
    // Legacy types
    booking: { icon: DollarSign, bg: 'bg-emerald-100', color: 'text-emerald-600' },
    visit: { icon: Calendar, bg: 'bg-blue-100', color: 'text-blue-600' },
    visit_scheduled: { icon: Calendar, bg: 'bg-blue-100', color: 'text-blue-600' },
    followup: { icon: MessageCircle, bg: 'bg-amber-100', color: 'text-amber-600' },
    like: { icon: Heart, bg: 'bg-pink-100', color: 'text-pink-600' },
    reaction: { icon: Flame, bg: 'bg-orange-100', color: 'text-orange-600' },
    comment: { icon: MessageCircle, bg: 'bg-purple-100', color: 'text-purple-600' },
    leaderboard: { icon: Trophy, bg: 'bg-amber-100', color: 'text-amber-600' },
    mention: { icon: User, bg: 'bg-indigo-100', color: 'text-indigo-600' },
  };
  
  return iconMap[type] || { icon: Bell, bg: 'bg-slate-100', color: 'text-slate-600' };
};

// Format relative time
const formatTimeAgo = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  const diff = Math.floor((now - date) / 1000);
  
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

// Tabs configuration
const TABS = [
  { id: 'all', label: 'All', icon: Bell },
  { id: 'action', label: 'Action Required', icon: AlertCircle },
  { id: 'social', label: 'Social', icon: Users },
];

const Notifications = ({ user }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [counts, setCounts] = useState({ total: 0, action: 0, social: 0, high_priority: 0 });
  const [swipedId, setSwipedId] = useState(null);
  const touchStartX = useRef(0);

  // Fetch notifications
  const fetchNotifications = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const category = activeTab === 'all' ? null : activeTab;
      const [notifResponse, countResponse] = await Promise.all([
        api.getNotifications({ limit: 50, category }),
        api.getNotificationCount()
      ]);
      
      setNotifications(notifResponse.data || []);
      setCounts(countResponse.data || { total: 0, action: 0, social: 0, high_priority: 0 });
    } catch (error) {
      console.error('Failed to load notifications:', error);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Polling for new notifications (60s in foreground)
  useEffect(() => {
    const pollInterval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchNotifications(true);
      }
    }, 60000); // 60 seconds

    return () => clearInterval(pollInterval);
  }, [fetchNotifications]);

  const handleMarkAsRead = async (notificationId) => {
    try {
      await api.markNotificationRead(notificationId);
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? { ...n, read: true } : n)
      );
      setCounts(prev => ({ ...prev, total: Math.max(0, prev.total - 1) }));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    try {
      const category = activeTab === 'all' ? null : activeTab;
      await api.markAllNotificationsRead(category);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      
      if (activeTab === 'all') {
        setCounts(prev => ({ ...prev, total: 0, action: 0, social: 0 }));
      } else {
        setCounts(prev => ({ ...prev, total: prev.total - prev[activeTab], [activeTab]: 0 }));
      }
      
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    } finally {
      setMarkingAll(false);
    }
  };

  const handleDismiss = async (notification) => {
    // Only LOW priority can be dismissed
    if (notification.priority === 'high') {
      toast.error('High priority notifications require action');
      return;
    }
    
    try {
      await api.dismissNotification(notification.notification_id);
      setNotifications(prev => prev.filter(n => n.notification_id !== notification.notification_id));
      toast.success('Notification dismissed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to dismiss');
    }
    setSwipedId(null);
  };

  const handleNotificationClick = (notification) => {
    // Mark as read
    if (!notification.read) {
      handleMarkAsRead(notification.notification_id);
    }
    
    // Navigate to deep link
    const deepLink = notification.deep_link;
    if (deepLink) {
      navigate(deepLink);
      return;
    }
    
    // Fallback navigation based on entity
    if (notification.post_id || notification.entity_type === 'post') {
      navigate('/feed');
    } else if (notification.customer_id || notification.entity_type === 'customer') {
      navigate(`/crm/${notification.customer_id || notification.entity_id}`);
    } else if (notification.booking_id || notification.entity_type === 'booking') {
      navigate(`/bookings/${notification.booking_id || notification.entity_id}`);
    } else if (notification.entity_type === 'followup') {
      navigate(`/crm/${notification.metadata?.customer_id || notification.customer_id}`);
    }
  };

  // Touch handlers for swipe to dismiss
  const handleTouchStart = (e, notificationId) => {
    touchStartX.current = e.touches[0].clientX;
    setSwipedId(null);
  };

  const handleTouchEnd = (e, notification) => {
    const touchEndX = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX;
    
    // Swipe left to reveal dismiss (only for non-high priority)
    if (diff > 80 && notification.priority !== 'high') {
      setSwipedId(notification.notification_id);
    } else {
      setSwipedId(null);
    }
  };

  const getUnreadCountForTab = (tab) => {
    if (tab === 'all') return counts.total;
    return counts[tab] || 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-[#5B3DF5] mx-auto mb-3" />
          <p className="text-sm text-slate-500">Loading notifications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[100dvh] bg-[#F7F8FA]">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 -ml-2 rounded-full hover:bg-slate-100 transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft size={20} className="text-slate-600" />
            </button>
            <div>
              <h1 className="text-lg font-bold text-[#111827]">Notifications</h1>
              {counts.high_priority > 0 && (
                <p className="text-xs text-red-600 font-medium">{counts.high_priority} urgent</p>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {getUnreadCountForTab(activeTab) > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleMarkAllRead}
                disabled={markingAll}
                className="text-xs text-[#5B3DF5] hover:bg-purple-50"
              >
                <CheckCheck size={14} className="mr-1" />
                {markingAll ? 'Marking...' : 'Mark all read'}
              </Button>
            )}
            <button 
              onClick={() => fetchNotifications(true)}
              disabled={refreshing}
              className="p-2 rounded-full hover:bg-slate-100 transition-colors"
            >
              <RefreshCw size={18} className={`text-slate-600 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            <MobileMenuTrigger user={user} />
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-3 -mx-1">
          {TABS.map((tab) => {
            const count = getUnreadCountForTab(tab.id);
            const TabIcon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 px-3 py-2 rounded-xl text-xs font-medium transition-all flex items-center justify-center gap-1.5 ${
                  activeTab === tab.id
                    ? 'bg-[#5B3DF5] text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <TabIcon size={14} />
                {tab.label}
                {count > 0 && (
                  <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                    activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-[#5B3DF5] text-white'
                  }`}>
                    {count > 99 ? '99+' : count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Notifications List */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2">
        {notifications.length === 0 ? (
          <Card className="p-8 text-center bg-white rounded-2xl">
            <Bell size={40} className="text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">No notifications</p>
            <p className="text-sm text-slate-400 mt-1">
              {activeTab === 'action' 
                ? "You're all caught up! No actions needed."
                : activeTab === 'social'
                ? "No social activity yet."
                : "You'll see updates here when there's activity"}
            </p>
          </Card>
        ) : (
          notifications.map((notification) => {
            const iconConfig = getNotificationIcon(notification.type, notification.priority);
            const IconComponent = iconConfig.icon;
            const priorityConfig = PRIORITY_CONFIG[notification.priority] || PRIORITY_CONFIG.medium;
            const isSwiped = swipedId === notification.notification_id;
            
            return (
              <div 
                key={notification.notification_id}
                className="relative overflow-hidden rounded-2xl"
                data-testid={`notification-${notification.notification_id}`}
              >
                {/* Dismiss button (revealed on swipe) */}
                {notification.priority !== 'high' && (
                  <div className={`absolute right-0 top-0 bottom-0 w-20 bg-red-500 flex items-center justify-center transition-opacity ${
                    isSwiped ? 'opacity-100' : 'opacity-0 pointer-events-none'
                  }`}>
                    <button
                      onClick={() => handleDismiss(notification)}
                      className="text-white p-2"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                )}
                
                <button
                  onClick={() => handleNotificationClick(notification)}
                  onTouchStart={(e) => handleTouchStart(e, notification.notification_id)}
                  onTouchEnd={(e) => handleTouchEnd(e, notification)}
                  className={`w-full text-left p-4 transition-all duration-200 border-l-4 ${
                    notification.read 
                      ? 'bg-white hover:bg-slate-50 border-l-transparent' 
                      : priorityConfig.color
                  } ${isSwiped ? '-translate-x-20' : 'translate-x-0'}`}
                  style={{ transition: 'transform 0.2s ease-out' }}
                >
                  <div className="flex gap-3">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${iconConfig.bg}`}>
                      <IconComponent size={18} className={iconConfig.color} />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm ${notification.read ? 'text-slate-700' : 'text-slate-900 font-medium'}`}>
                          {notification.title}
                        </p>
                        {!notification.read && notification.priority === 'high' && (
                          <span className="flex-shrink-0 px-1.5 py-0.5 text-[10px] font-bold bg-red-100 text-red-700 rounded">
                            URGENT
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                        {notification.message}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-xs text-slate-400">
                          {formatTimeAgo(notification.created_at)}
                        </p>
                        {notification.group_count > 1 && (
                          <span className="text-xs text-slate-400">
                            • +{notification.group_count - 1} more
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {!notification.read && (
                      <div className="w-2 h-2 bg-[#5B3DF5] rounded-full flex-shrink-0 mt-2"></div>
                    )}
                  </div>
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Notifications;
